/**
 * Group Classes — user-facing calendar of live Zoom sessions + shared recordings
 * (1-on-1 calls live under Teacher Feedback). Backend annotates each event with `is_locked` (true for
 * Free users on Pro-only events); a locked click opens the upgrade prompt.
 * All headings / captions come from Admin → Group Classes → Page text.
 */
import React, { useCallback, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/Layout';
import api from '../lib/api';
import { goPro } from '../lib/checkout';
import { usePageCopy, fill } from '../lib/pageCopy';
import { useTrial } from '../contexts/TrialContext';
import CalendarShell, { EventDetailModal } from '../components/Calendar';
import { LiveNowBanner } from '../components/events/LiveNowBanner';
import { RecordingsList } from '../components/events/RecordingsList';
import { Lock, Calendar as CalendarIcon, PlayCircle } from 'lucide-react';

export default function GroupClasses() {
  const c = usePageCopy('group_classes');
  const TABS = [['classes', c.tab_classes, CalendarIcon], ['recordings', c.tab_recordings, PlayCircle]];
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'recordings' ? 'recordings' : 'classes';
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [lockedAlert, setLockedAlert] = useState(null);  // event clicked while locked
  const navigate = useNavigate();

  const fetchEvents = useCallback((from, to) => {
    setLoading(true);
    api.get(`/api/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`)
      .then(r => setEvents(r.data?.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  const handleEventClick = (ev) => {
    if (ev?.is_locked) {
      setLockedAlert(ev);
      return;
    }
    setSelected(ev);
  };

  // 1-on-1 calls moved to Teacher Feedback (iter 131) — old links keep working.
  if (params.get('tab') === 'calls') return <Navigate to="/feedback-credits?tab=calls" replace />;

  return (
    <DashboardLayout title={c.title}>
      <div className="space-y-4" data-testid="group-classes-page">
        <div className="flex gap-1 border-b border-slate-200 -mt-1 sm:-mt-2 overflow-x-auto" data-testid="group-classes-tabs">
          {TABS.map(([k, label, Icon]) => (
            <button key={k} type="button" onClick={() => setParams(k === 'classes' ? {} : { tab: k })}
              className={`inline-flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition whitespace-nowrap ${tab === k ? 'border-[#2563EB] text-[#2563EB]' : 'border-transparent text-slate-500 hover:text-slate-800'}`} data-testid={`group-classes-tab-${k}`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
        {tab === 'recordings' ? <RecordingsList copy={c} /> : (
          <>
            <p className="text-xs sm:text-sm text-slate-500 max-w-2xl" data-testid="group-classes-intro">{c.intro}</p>
            <LiveNowBanner onLocked={setLockedAlert} />
            <CalendarShell
              events={events}
              loading={loading}
              onEventClick={handleEventClick}
              onRangeChange={fetchEvents}
            />
            <EventDetailModal event={selected} onClose={() => setSelected(null)} isAdmin={false} />
            {lockedAlert && (
              <LockedUpgradeModal
                event={lockedAlert}
                copy={c}
                onClose={() => setLockedAlert(null)}
                onUpgrade={() => goPro(navigate)}
              />
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function LockedUpgradeModal({ event, copy: c, onClose, onUpgrade }) {
  const { trialMode } = useTrial();
  const bullets = String(c.locked_bullets || '').split('\n').map((s) => s.trim()).filter(Boolean);
  const dismiss = trialMode && (!c.locked_dismiss || c.locked_dismiss === 'Maybe later') ? 'Not now — keep exploring my trial' : c.locked_dismiss;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="locked-event-upgrade-modal"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
      >
        <div className="bg-gradient-to-br from-amber-50 via-amber-100 to-orange-100 px-6 py-8 text-center border-b border-amber-200">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white shadow-sm mb-3">
            <Lock size={24} className="text-amber-600" />
          </div>
          <h3 className="text-xl font-bold text-slate-900">{c.locked_title}</h3>
          <p className="text-sm text-slate-600 mt-1 max-w-xs mx-auto">{fill(c.locked_body, { title: event.title })}</p>
        </div>
        <div className="px-6 py-5 space-y-4">
          <ul className="space-y-2 text-sm text-slate-700">
            {bullets.map((b) => <li key={b} className="flex items-start gap-2"><span className="text-amber-500">★</span> {b}</li>)}
          </ul>
          <div className="flex flex-col gap-2 pt-2">
            <button
              onClick={onUpgrade}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 rounded-lg transition"
              data-testid="locked-event-upgrade-btn"
            >
              {c.locked_cta}
            </button>
            <button
              onClick={onClose}
              className="w-full text-sm text-slate-500 hover:text-slate-700 py-2 transition"
              data-testid="locked-event-close-btn"
            >
              {dismiss}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
